import { ENDPOINT_KEY_PATTERN } from "./endpointKey";
import { REQUEST_ID_PATTERN } from "./requestId";
import {
  fetchActivityConfig,
  sendActivityEvents,
  type ActivityClientConfig,
  type ActivityErrorCode,
  type ActivityEventPayload,
  type ActivitySendResult,
} from "../hooks/api/activity";
import { resolveScreenKey, UNMAPPED_SCREEN_KEY } from "./activityScreens";
import type { ActivityActionKey } from "./activityCatalog.generated";
import { getAppVersion } from "./appVersion";

/**
 * Tracker de actividad de Psi Platform (Métricas de Actividad, Fase 2). Plan completo:
 * psima/docs/metricas-actividad/PLAN.md §5.3.
 *
 * Qué captura: pantallas visitadas (`screen_view`/`screen_leave` con tiempo ACTIVO), sesiones de
 * uso, acciones del catálogo y errores con código cerrado. Qué NO captura, nunca: contenido
 * clínico, datos de pacientes, texto libre (ni lo buscado), query params, el pathname crudo.
 *
 * Principios de diseño:
 *  - Fire-and-forget: ninguna llamada pública tira ni bloquea la UI; todo error se traga.
 *  - Liviano con el backend: cola en memoria, lote cada ~15 s (con jitter, para que las
 *    psicólogas no coincidan en el mismo segundo) o a los 20 eventos; flush con `keepalive` al
 *    ocultar/cerrar la pestaña.
 *  - Protegido ante un backend caído o sobrecargado: backoff exponencial ante 429/5xx/red, y tras
 *    5 fallos seguidos pausa 10 min descartando la cola (nunca reintenta sin tope).
 *  - Kill switch: respeta `GET /api/activity/config` (se relee cada 10 min) y `tracked:false`.
 *
 * Esta clase NO toca el DOM ni el router: recibe las señales por métodos (`onRouteChange`,
 * `markInput`, `onVisibilityHidden`, `onPageHide`, …) que cablea `activityDom.ts` / el hook.
 * Eso la hace testeable con relojes falsos.
 */

// ─── Constantes (todas con su porqué) ────────────────────────────────────────

/** Una sesión de uso termina tras 30 min sin actividad. */
export const SESSION_IDLE_MS = 30 * 60_000;
/** Sin input por 60 s, el tiempo deja de contar como "activo" (la pestaña quedó abierta sola). */
export const INPUT_IDLE_MS = 60_000;
/** Cada cuánto se acumula tiempo activo y se chequea el vencimiento de la sesión. */
export const TICK_MS = 5_000;
/** Intervalo de flush base; se le aplica ±33 % de jitter. */
export const FLUSH_BASE_MS = 15_000;
/** Con tantos eventos en cola se manda sin esperar al timer. */
export const FLUSH_AT_EVENTS = 20;
/** Tope de la cola en memoria: si se llena (backend caído), se descartan los más viejos. */
export const MAX_QUEUE = 200;
/** Tope de eventos por sesión de uso (protección ante un loop de emisión). */
export const MAX_SESSION_EVENTS = 2_000;
/** Misma acción en la misma pantalla dentro de esta ventana se ignora. */
export const DEBOUNCE_MS = 1_000;

/** Única acción que lleva `ref` (id opaco del aviso de CX); el backend la exige y no admite otra. */
const REF_ACTION_KEY = "notification_link_clicked";
const REF_PATTERN = /^[A-Za-z0-9]{1,32}$/;

export interface ActionExtra {
  resultsCount?: number;
  hadResults?: boolean;
  ref?: string;
}
/** Acciones que se guardan mientras todavía no se resolvió el config (tope: no es una cola real). */
export const MAX_PENDING_ACTIONS = 10;
/** Errores DISTINTOS (pantalla + código + endpoint + status) que se registran por minuto. Las repeticiones del
 *  mismo error no cuentan acá: se agrupan en un solo evento con `occurrences` (ver `ERROR_GROUP_MS`). */
export const MAX_ERRORS_PER_MINUTE = 10;
/** Ventana en la que las repeticiones del mismo error se suman en un solo evento. Una pantalla que falla suele
 *  disparar varias llamadas a la vez: antes el tope por minuto descartaba la ráfaga y se subcontaba. */
export const ERROR_GROUP_MS = 5_000;
/** Tope de repeticiones por evento (el backend rechaza más): una ráfaga que pasa de esto se cuenta como 50. */
export const MAX_ERROR_OCCURRENCES = 50;

export type ErrorClient = "api-core" | "psiplatform-be" | "other";
/** Detalle sanitizado de un error HTTP. El tracker lo vuelve a validar: nada de esto puede traer texto libre. */
export interface ErrorDetail {
  httpStatus?: number;
  endpointKey?: string;
  client?: ErrorClient;
  durationMs?: number;
  retried?: boolean;
  requestId?: string;
}
const ERROR_CLIENTS: readonly string[] = ["api-core", "psiplatform-be", "other"];

function cleanErrorDetail(d: ErrorDetail | undefined): Partial<ActivityEventPayload> {
  const out: Partial<ActivityEventPayload> = {};
  if (!d) return out;
  if (Number.isInteger(d.httpStatus) && d.httpStatus! >= 400 && d.httpStatus! <= 599) out.httpStatus = d.httpStatus;
  if (typeof d.endpointKey === "string" && ENDPOINT_KEY_PATTERN.test(d.endpointKey)) out.endpointKey = d.endpointKey;
  if (typeof d.client === "string" && ERROR_CLIENTS.includes(d.client)) out.client = d.client;
  if (typeof d.durationMs === "number" && Number.isFinite(d.durationMs)) {
    out.durationMs = Math.max(0, Math.min(86_400_000, Math.round(d.durationMs)));
  }
  if (typeof d.retried === "boolean") out.retried = d.retried;
  if (typeof d.requestId === "string" && REQUEST_ID_PATTERN.test(d.requestId)) out.requestId = d.requestId;
  return out;
}

interface ErrorGroup {
  screenKey: string;
  errorCode: ActivityErrorCode;
  detail?: ErrorDetail;
  count: number;
  timer: ReturnType<typeof setTimeout>;
}
/** Cada cuánto se vuelve a leer el config del backend (kill switch sin recargar la página). */
export const CONFIG_RECHECK_MS = 10 * 60_000;
/** Reintento corto cuando el config no se pudo leer (red/5xx/timeout), a diferencia del chequeo normal. */
export const CONFIG_RETRY_MS = 2 * 60_000;
export const BACKOFF_START_MS = 30_000;
export const BACKOFF_MAX_MS = 5 * 60_000;
/** Fallos seguidos que disparan la pausa larga (y el descarte de la cola). */
export const MAX_CONSECUTIVE_FAILURES = 5;
export const PAUSE_MS = 10 * 60_000;

const STORAGE_KEY = "psi-activity-v1";

// ─── Dependencias inyectables (para testear) ─────────────────────────────────

export interface TrackerDeps {
  now: () => number;
  random: () => number;
  uuid: () => string;
  isVisible: () => boolean;
  viewportClass: () => ActivityEventPayload["viewportClass"];
  /** Versión del frontend (hash del bundle); opcional: sin ella los eventos van sin `appVersion`. */
  appVersion?: () => string | undefined;
  readStorage: () => string | null;
  writeStorage: (value: string) => void;
  fetchConfig: () => Promise<ActivityClientConfig | null>;
  send: (events: ActivityEventPayload[], opts: { keepalive: boolean }) => Promise<ActivitySendResult>;
}

const fallbackUuid = (): string =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

export const defaultDeps = (): TrackerDeps => ({
  now: () => Date.now(),
  random: () => Math.random(),
  uuid: () =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : fallbackUuid(),
  isVisible: () => typeof document === "undefined" || document.visibilityState !== "hidden",
  appVersion: getAppVersion,
  viewportClass: () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1280;
    return w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
  },
  readStorage: () => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  },
  writeStorage: (value) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* sin storage (modo privado, etc.): la sesión vive solo en memoria */
    }
  },
  fetchConfig: fetchActivityConfig,
  send: sendActivityEvents,
});

interface StoredSession {
  sessionId: string;
  tabId: string;
  lastInputAt: number;
}

interface CurrentScreen {
  key: string;
  activeMs: number;
}

// ─── Tracker ─────────────────────────────────────────────────────────────────

export class ActivityTracker {
  private started = false;
  private enabled = false;
  /** Ya se leyó el config del backend (aunque diga que está apagado). Hasta entonces las acciones se guardan. */
  private configResolved = false;
  /** Acciones que ocurrieron ANTES de resolver el config (ej. el retorno de un OAuth al montar la pantalla,
   *  cuyo efecto corre antes que el `start()` del AppShell). Se reproducen al prender y se descartan si no. */
  private pendingActions: Array<{ actionKey: ActivityActionKey; extra?: ActionExtra }> = [];
  /** 401/403: no se vuelve a intentar hasta recargar la página. */
  private dead = false;
  private config: ActivityClientConfig = {
    enabled: false,
    sampleRate: 0,
    flushIntervalMs: FLUSH_BASE_MS,
    maxBatchSize: 50,
  };

  private sessionId = "";
  private tabId = "";
  private sessionActiveMs = 0;
  private sessionEvents = 0;
  private sessionOpen = false;
  private lastInputAt = 0;
  private lastTickAt = 0;

  /** Última ruta informada por el router (puede llegar antes de que el config esté listo). */
  private pendingKey: string | null = null;
  private current: CurrentScreen | null = null;

  private queue: ActivityEventPayload[] = [];
  /** Envíos en vuelo. Un flush normal espera a que no haya ninguno; uno con keepalive (la página se va) no. */
  private inFlight = 0;
  private failures = 0;
  private backoffMs = BACKOFF_START_MS;
  private lastEmit = new Map<string, number>();
  private errorTimes: number[] = [];
  /** Errores recientes agrupándose (clave = pantalla|código|endpoint|status) hasta que vence `ERROR_GROUP_MS`. */
  private pendingErrors = new Map<string, ErrorGroup>();

  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private configTimer: ReturnType<typeof setTimeout> | null = null;
  private resumeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly deps: TrackerDeps = defaultDeps()) {}

  // ── Ciclo de vida ──────────────────────────────────────────────────────────

  /** Idempotente. Lee el config del backend; si la captura está apagada, no hace nada más
   *  (salvo reintentar leer el config cada 10 min, por si la prenden). */
  async start(): Promise<void> {
    if (this.started || this.dead) return;
    this.started = true;
    try {
      await this.loadConfig();
    } catch {
      /* nunca tira */
    }
  }

  stop(): void {
    try {
      if (this.enabled) {
        this.flushPendingErrors();
        this.leaveCurrent();
        void this.flush({ keepalive: true });
      }
      this.disable();
      this.clearTimer("config");
      this.started = false;
      this.configResolved = false;
      this.pendingActions = [];
      // Un nuevo montaje (ej. el login siguiente a un 401) puede volver a intentar: si el token sigue inválido,
      // el primer 401/403 lo apaga de nuevo.
      this.dead = false;
    } catch {
      /* nunca tira */
    }
  }

  private async loadConfig(): Promise<void> {
    const cfg = await this.deps.fetchConfig();
    if (!this.started || this.dead) return;
    if (cfg === null) {
      // No se pudo LEER el config (red, 5xx, timeout): no es lo mismo que "apagado". Se conserva el estado (si
      // estaba capturando, sigue; si no, las acciones previas siguen guardadas) y se reintenta pronto, en vez de
      // descartar la cola y quedar sin captura hasta el próximo chequeo.
      this.scheduleConfigRecheck(CONFIG_RETRY_MS);
      return;
    }
    this.configResolved = true;
    if (cfg?.enabled) {
      this.config = cfg;
      if (!this.enabled) this.enable();
    } else if (this.enabled) {
      this.disable();
    }
    // Acciones previas al config: se reproducen si quedó prendido; si no, se descartan.
    const pending = this.pendingActions;
    this.pendingActions = [];
    if (this.enabled) for (const a of pending) this.trackAction(a.actionKey, a.extra);
    this.scheduleConfigRecheck();
  }

  private scheduleConfigRecheck(delayMs: number = CONFIG_RECHECK_MS): void {
    this.clearTimer("config");
    this.configTimer = setTimeout(() => {
      void this.loadConfig().catch(() => undefined);
    }, delayMs);
  }

  private enable(): void {
    this.enabled = true;
    this.beginSession();
    this.lastTickAt = this.deps.now();
    this.tickTimer = setInterval(() => this.tick(), TICK_MS);
    if (this.pendingKey) this.enterScreen(this.pendingKey);
  }

  /** Apaga la captura sin tocar el config timer (que puede volver a prenderla). */
  private disable(): void {
    this.enabled = false;
    this.sessionOpen = false;
    this.current = null;
    this.queue = [];
    this.dropPendingErrors();
    this.clearTimer("tick");
    this.clearTimer("flush");
    this.clearTimer("resume");
  }

  // ── Señales de afuera ──────────────────────────────────────────────────────

  /** Cambio de ruta del router. Solo recibe el pathname (nunca query ni hash). */
  onRouteChange(pathname: string): void {
    try {
      const key = resolveScreenKey(pathname);
      this.pendingKey = key;
      if (!this.enabled || !this.sessionOpen) return;
      if (this.current?.key === key) return; // misma pantalla (ej. cambió solo el query): no es una visita nueva
      this.leaveCurrent();
      this.enterScreen(key);
    } catch {
      /* nunca tira */
    }
  }

  /** Hubo input del usuario (mouse/teclado/scroll/touch). Quien llama ya lo throttlea. */
  markInput(): void {
    try {
      if (!this.enabled) return;
      const now = this.deps.now();
      if (this.sessionOpen && now - this.lastInputAt >= SESSION_IDLE_MS) {
        // El tick de 5 s no corrió (equipo suspendido o timers pausados) y la sesión sigue "abierta" con una pausa
        // larga adentro: se cierra acá (sin acreditar la pausa: `lastInputAt` todavía es el viejo) y abre una nueva.
        this.lastTickAt = now;
        this.endSession();
      }
      this.lastInputAt = now;
      if (!this.sessionOpen) {
        // Volvió después de una pausa larga: arranca una sesión nueva en la pantalla actual.
        this.beginSession();
        if (this.pendingKey) this.enterScreen(this.pendingKey);
      }
    } catch {
      /* nunca tira */
    }
  }

  /** La pestaña se ocultó: se vacía la cola (con keepalive) por si no vuelve. */
  onVisibilityHidden(): void {
    try {
      if (this.enabled) {
        this.flushPendingErrors();
        void this.flush({ keepalive: true });
      }
    } catch {
      /* nunca tira */
    }
  }

  /** La pestaña se está cerrando/navegando afuera: cierra la pantalla (duración) y vacía. */
  onPageHide(): void {
    try {
      if (!this.enabled) return;
      this.flushPendingErrors();
      this.leaveCurrent();
      void this.flush({ keepalive: true });
    } catch {
      /* nunca tira */
    }
  }

  /** Volvió de la caché de ida y vuelta (bfcache): se reabre la pantalla actual. */
  onPageShow(persisted: boolean): void {
    try {
      if (!persisted || !this.enabled || !this.sessionOpen || !this.pendingKey) return;
      if (!this.current) this.enterScreen(this.pendingKey);
    } catch {
      /* nunca tira */
    }
  }

  /** Acción de negocio del catálogo cerrado (ej. "search_patients_list"). Solo claves, nunca texto. */
  /** `actionKey` es una clave del catálogo (tipada y generada desde el backend): una que no exista no compila. */
  trackAction(actionKey: ActivityActionKey, extra?: ActionExtra): void {
    try {
      if (!this.configResolved) {
        if (this.pendingActions.length < MAX_PENDING_ACTIONS) this.pendingActions.push({ actionKey, extra });
        return;
      }
      if (!this.enabled || !this.sessionOpen) return;
      const screenKey = this.current?.key ?? this.pendingKey ?? UNMAPPED_SCREEN_KEY;
      // `notification_link_clicked` exige un ref válido (id del aviso): sin él no se emite (el backend lo rechazaría).
      const ref = extra?.ref;
      if (actionKey === REF_ACTION_KEY && (typeof ref !== "string" || !REF_PATTERN.test(ref))) return;
      // Dos avisos distintos tocados seguidos son dos clics: el ref entra en la clave del antirrebote.
      if (this.isDebounced(`action:${screenKey}:${actionKey}${ref ? `:${ref}` : ""}`)) return;
      const payload: Partial<ActivityEventPayload> = { actionKey };
      if (actionKey === REF_ACTION_KEY && ref) payload.ref = ref;
      if (extra?.resultsCount !== undefined) {
        payload.resultsCount = Math.max(0, Math.min(100_000, Math.round(extra.resultsCount)));
      }
      if (extra?.hadResults !== undefined) payload.hadResults = extra.hadResults;
      this.emit("action", screenKey, payload);
    } catch {
      /* nunca tira */
    }
  }

  /**
   * Error con código cerrado (sin mensaje) y, si es una llamada HTTP, su detalle sanitizado. Las repeticiones del
   * mismo error (misma pantalla, código, endpoint y status) dentro de `ERROR_GROUP_MS` salen en UN evento con
   * `occurrences`; se registran hasta `MAX_ERRORS_PER_MINUTE` errores distintos por minuto.
   */
  trackError(errorCode: ActivityErrorCode, detail?: ErrorDetail): void {
    try {
      if (!this.enabled || !this.sessionOpen) return;
      const screenKey = this.current?.key ?? this.pendingKey ?? UNMAPPED_SCREEN_KEY;
      const key = [screenKey, errorCode, detail?.endpointKey ?? "", detail?.httpStatus ?? ""].join("|");
      const group = this.pendingErrors.get(key);
      if (group) {
        group.count += 1;
        if (detail?.retried) group.detail = { ...group.detail, retried: true };
        return;
      }
      const now = this.deps.now();
      this.errorTimes = this.errorTimes.filter((t) => now - t < 60_000);
      if (this.errorTimes.length >= MAX_ERRORS_PER_MINUTE) return;
      this.errorTimes.push(now);
      const timer = setTimeout(() => this.flushErrorGroup(key), ERROR_GROUP_MS);
      this.pendingErrors.set(key, { screenKey, errorCode, detail, count: 1, timer });
    } catch {
      /* nunca tira */
    }
  }

  private flushErrorGroup(key: string): void {
    try {
      const group = this.pendingErrors.get(key);
      if (!group) return;
      clearTimeout(group.timer);
      this.pendingErrors.delete(key);
      if (!this.enabled || !this.sessionOpen) return;
      this.emit("error", group.screenKey, {
        errorCode: group.errorCode,
        ...cleanErrorDetail(group.detail),
        ...(group.count > 1 ? { occurrences: Math.min(group.count, MAX_ERROR_OCCURRENCES) } : {}),
      });
    } catch {
      /* nunca tira */
    }
  }

  /** Emite ya los errores que todavía se estaban agrupando (la pestaña se oculta o se cierra, o termina la sesión). */
  private flushPendingErrors(): void {
    Array.from(this.pendingErrors.keys()).forEach((key) => this.flushErrorGroup(key));
  }

  private dropPendingErrors(): void {
    this.pendingErrors.forEach((group) => clearTimeout(group.timer));
    this.pendingErrors.clear();
  }

  // ── Sesión y pantallas ─────────────────────────────────────────────────────

  private beginSession(): void {
    const now = this.deps.now();
    const stored = this.readStored();
    if (stored && now - stored.lastInputAt < SESSION_IDLE_MS) {
      // Recarga dentro de la misma sesión de uso: se continúa (no hay session_start nuevo).
      this.sessionId = stored.sessionId;
      this.tabId = stored.tabId;
      this.sessionOpen = true;
      this.lastInputAt = now;
      this.persist();
      return;
    }
    this.sessionId = this.deps.uuid();
    this.tabId = stored?.tabId ?? this.deps.uuid();
    this.sessionActiveMs = 0;
    this.sessionEvents = 0;
    this.sessionOpen = true;
    this.lastInputAt = now;
    this.persist();
    this.emit("session_start", this.pendingKey ?? "home", {});
  }

  /** Cierra la sesión de uso por inactividad: leave de la pantalla + session_end con el tiempo activo. */
  private endSession(): void {
    this.flushPendingErrors();
    this.leaveCurrent();
    this.emit("session_end", this.pendingKey ?? "home", {
      durationMs: this.clampDuration(this.sessionActiveMs),
    });
    this.sessionOpen = false;
    void this.flush({ keepalive: false });
  }

  private enterScreen(key: string): void {
    this.current = { key, activeMs: 0 };
    this.emit("screen_view", key, {});
  }

  private leaveCurrent(): void {
    if (!this.current) return;
    // Suma lo transcurrido desde el último tick: sin esto, una visita más corta que el tick (5 s) que no
    // cruza un tick quedaba en 0 ms y subestimaba SISTEMÁTICAMENTE el tiempo de las pantallas "de paso".
    this.accrueSinceLastTick();
    const { key, activeMs } = this.current;
    this.current = null;
    this.emit("screen_leave", key, { durationMs: this.clampDuration(activeMs) });
  }

  /** Acumula el tiempo activo transcurrido desde la última marca (solo con la pestaña visible y con
   *  input reciente), con `capMs` como tope por si el navegador espació los timers. */
  private accrue(now: number, capMs: number): void {
    const dt = Math.min(Math.max(0, now - this.lastTickAt), capMs);
    this.lastTickAt = now;
    if (!this.enabled || !this.sessionOpen) return;
    if (this.deps.isVisible() && now - this.lastInputAt < INPUT_IDLE_MS) {
      if (this.current) this.current.activeMs += dt;
      this.sessionActiveMs += dt;
    }
  }

  /** Al cerrar una pantalla: acredita el tramo parcial desde el último tick (máx. un tick). */
  private accrueSinceLastTick(): void {
    this.accrue(this.deps.now(), TICK_MS);
  }

  /** Cada 5 s: acumula tiempo activo y detecta el vencimiento de la sesión. */
  private tick(): void {
    try {
      const now = this.deps.now();
      if (this.enabled && this.sessionOpen && now - this.lastInputAt >= SESSION_IDLE_MS) {
        this.lastTickAt = now;
        this.endSession();
        return;
      }
      // Tope: con la pestaña en segundo plano el navegador espacia los timers, y un tick tardío
      // no debe sumar de golpe minutos que no fueron de uso.
      this.accrue(now, TICK_MS * 2);
      if (this.enabled && this.sessionOpen) this.persist();
    } catch {
      /* nunca tira */
    }
  }

  // ── Cola y envío ───────────────────────────────────────────────────────────

  private emit(
    eventType: ActivityEventPayload["eventType"],
    screenKey: string,
    extra: Partial<ActivityEventPayload>,
  ): void {
    if (this.sessionEvents >= MAX_SESSION_EVENTS) return;
    this.sessionEvents += 1;
    const appVersion = this.deps.appVersion?.();
    const event: ActivityEventPayload = {
      eventId: this.deps.uuid(),
      sessionId: this.sessionId,
      tabId: this.tabId,
      eventType,
      screenKey,
      occurredAt: new Date(this.deps.now()).toISOString(),
      viewportClass: this.deps.viewportClass(),
      ...(appVersion ? { appVersion } : {}),
      ...extra,
    };
    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);

    if (this.queue.length >= FLUSH_AT_EVENTS) void this.flush({ keepalive: false });
    else this.scheduleFlush();
  }

  private isDebounced(key: string): boolean {
    const now = this.deps.now();
    const last = this.lastEmit.get(key);
    this.lastEmit.set(key, now);
    if (this.lastEmit.size > 200) this.lastEmit.clear();
    return last !== undefined && now - last < DEBOUNCE_MS;
  }

  private scheduleFlush(delayMs?: number): void {
    if (this.flushTimer || !this.enabled) return;
    // ±33 % de jitter: 500 psicólogas no deben coincidir en el mismo segundo.
    const base = delayMs ?? this.config.flushIntervalMs ?? FLUSH_BASE_MS;
    const jitter = delayMs === undefined ? 0.67 + this.deps.random() * 0.66 : 1;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush({ keepalive: false });
    }, base * jitter);
  }

  async flush(opts: { keepalive: boolean }): Promise<void> {
    try {
      if (!this.enabled || this.queue.length === 0) return;
      // Un flush normal no se superpone con otro. El de keepalive SÍ: ocurre cuando la pestaña se oculta o se
      // cierra, y el flush de `visibilitychange` suele seguir en vuelo cuando llega `pagehide`; si este
      // saliera sin enviar, el screen_leave (con el tiempo activo) se perdería con la página.
      if (this.inFlight > 0 && !opts.keepalive) return;
      this.clearTimer("flush");
      this.inFlight += 1;
      const batch = this.queue.splice(0, Math.max(1, this.config.maxBatchSize));
      let result: ActivitySendResult;
      try {
        result = await this.deps.send(batch, opts);
      } catch {
        result = { kind: "retry" };
      }
      this.inFlight -= 1;
      this.handleResult(batch, result);
    } catch {
      this.inFlight = Math.max(0, this.inFlight - 1);
    }
  }

  private handleResult(batch: ActivityEventPayload[], result: ActivitySendResult): void {
    if (!this.enabled) return;
    switch (result.kind) {
      case "ok":
        this.failures = 0;
        this.backoffMs = BACKOFF_START_MS;
        if (!result.tracked) {
          // El backend dice que no capturemos (kill switch / fuera del rollout): se apaga
          // y se vuelve a preguntar al config en 10 min.
          this.disable();
          return;
        }
        if (this.queue.length > 0) this.scheduleFlush(1_000);
        return;
      case "retry":
        this.requeue(batch);
        this.failures += 1;
        if (this.failures >= MAX_CONSECUTIVE_FAILURES) {
          // Backend caído o sobrecargado: no seguir insistiendo. Se descarta la cola y se pausa.
          this.queue = [];
          this.failures = 0;
          this.backoffMs = BACKOFF_START_MS;
          this.clearTimer("flush");
          this.clearTimer("resume");
          this.resumeTimer = setTimeout(() => {
            this.resumeTimer = null;
            if (this.queue.length > 0) this.scheduleFlush(1_000);
          }, PAUSE_MS);
          return;
        }
        this.scheduleFlush(this.backoffMs);
        this.backoffMs = Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
        return;
      case "auth":
        // Sesión inválida: se deja de trackear hasta recargar. Sin redirigir ni avisar.
        this.dead = true;
        this.disable();
        this.clearTimer("config");
        return;
      case "drop":
        // Lote rechazado de forma definitiva (ej. 400): se descarta, sin reintentar.
        if (this.queue.length > 0) this.scheduleFlush(1_000);
        return;
    }
  }

  private requeue(batch: ActivityEventPayload[]): void {
    this.queue = [...batch, ...this.queue];
    if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
  }

  // ── Utilidades ─────────────────────────────────────────────────────────────

  private clampDuration(ms: number): number {
    return Math.max(0, Math.min(86_400_000, Math.round(ms)));
  }

  private readStored(): StoredSession | null {
    try {
      const raw = this.deps.readStorage();
      if (!raw) return null;
      const v = JSON.parse(raw) as Partial<StoredSession>;
      if (typeof v.sessionId === "string" && typeof v.tabId === "string" && typeof v.lastInputAt === "number") {
        return { sessionId: v.sessionId, tabId: v.tabId, lastInputAt: v.lastInputAt };
      }
    } catch {
      /* storage corrupto: se ignora */
    }
    return null;
  }

  private persist(): void {
    this.deps.writeStorage(
      JSON.stringify({ sessionId: this.sessionId, tabId: this.tabId, lastInputAt: this.lastInputAt }),
    );
  }

  private clearTimer(which: "tick" | "flush" | "config" | "resume"): void {
    if (which === "tick" && this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    } else if (which === "flush" && this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    } else if (which === "config" && this.configTimer) {
      clearTimeout(this.configTimer);
      this.configTimer = null;
    } else if (which === "resume" && this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
  }
}

/** Instancia única de la app (el AppShell la arranca; los componentes llaman `trackAction`). */
export const activityTracker = new ActivityTracker();
